<template>
  <div ref="NeyslaDirectUsage" class="transparent">
    <h2>{{ $t("portfolio.neysla.directUsage.pageLabel") }}</h2>
    <hr class="secondary" />
    <p>{{ $t("portfolio.neysla.directUsage.paragraphDirectUsage") }}</p>
    <ul>
      <li>GET</li>
      <li>HEAD</li>
      <li>POST</li>
      <li>PATCH</li>
      <li>PUT</li>
      <li>DELETE</li>
    </ul>

    <h3>GET</h3>
    <pre v-highlight class="highlight">
      <code class="javascript">
...
  Neysla.get({
    url: "https://www.my-domain.com/api/your-endpoint?param=paramValue",
    responseType: "blob"
  }).then(data => console.log(data)).catch(error => console.log(error));
...
      </code>
    </pre>
    <p
      v-html="
        $t('portfolio.neysla.directUsage.paragraphAttributes', {
          method: 'GET',
          purple: 'text-white-purple'
        })
      "
    />
    <datatable :title="directRequestTitle" :data="directRequestNoBodyData"></datatable>
    <p>
      *
      <small>
        <a
          href="https://developer.mozilla.org/en-US/docs/Web/API/XMLHttpRequest/responseType"
          class="general-anchor decorated"
          rel="noreferrer,noopener"
          target="_blank"
        >
          <span>{{ $t("portfolio.neysla.ctaSupportedResponse") }}</span> </a
        >.
      </small>
    </p>

    <h3>HEAD</h3>
    <pre v-highlight class="highlight">
      <code class="javascript">
...
  Neysla.head({
    url: "https://www.my-domain.com/api/your-endpoint?param=paramValue"
  }).then(data => console.log(data)).catch(error => console.log(error));
...
      </code>
    </pre>
    <p
      v-html="
        $t('portfolio.neysla.paragraphSameAttributes', {
          method: 'GET',
          purple: 'text-white-purple'
        })
      "
    />

    <h3>POST</h3>
    <pre v-highlight class="highlight">
      <code class="javascript">
...
  Neysla.post({
    url: "https://www.my-domain.com/api/your-endpoint?param=paramValue",
    headers: {
      "Authentication": "Basic dufhspo43p43hu34h5p34hu5p34"
    },
    requestType: "multipart",
    body: {
      "address": "Saint street 177",
      "active": false,
      "first_name": "Roberto",
      "last_name": "Magallanez"
    }
  }).then(data => console.log(data)).catch(error => console.log(error));
...
      </code>
    </pre>
    <p
      v-html="
        $t('portfolio.neysla.directUsage.paragraphAttributes', {
          method: 'POST',
          purple: 'text-white-purple'
        })
      "
    />
    <datatable :title="directRequestTitle" :data="directRequestBodyData"></datatable>
    <p>
      *
      <small>
        <a
          href="https://developer.mozilla.org/en-US/docs/Web/API/XMLHttpRequest/responseType"
          class="general-anchor decorated"
          rel="noreferrer,noopener"
          target="_blank"
        >
          <span>{{ $t("portfolio.neysla.ctaSupportedResponse") }}</span> </a
        >.
      </small>
    </p>

    <h3>PATCH</h3>
    <pre v-highlight class="highlight">
      <code class="javascript">
...
  Neysla.patch({
    url: "https://www.my-domain.com/api/your-endpoint?param=paramValue",
    requestType: "multipart",
    body: {
      "active": true
    }
  }).then(data => console.log(data)).catch(error => console.log(error));
...
      </code>
    </pre>
    <p
      v-html="
        $t('portfolio.neysla.paragraphSameAttributes', {
          method: 'POST',
          purple: 'text-white-purple'
        })
      "
    />

    <h3>PUT</h3>
    <pre v-highlight class="highlight">
      <code class="javascript">
...
  Neysla.put({
    url: "https://www.my-domain.com/api/your-endpoint?param=paramValue",
    body: {
      "active": true
    }
  }).then(data => console.log(data)).catch(error => console.log(error));
...
      </code>
    </pre>
    <p
      v-html="
        $t('portfolio.neysla.paragraphSameAttributes', {
          method: 'POST',
          purple: 'text-white-purple'
        })
      "
    />

    <h3>DELETE</h3>
    <pre v-highlight class="highlight">
      <code class="javascript">
...
  Neysla.remove({
    url: "https://www.my-domain.com/api/your-endpoint?param=paramValue",
    body: {
      "active": true
    }
  }).then(data => console.log(data)).catch(error => console.log(error));
...
      </code>
    </pre>
    <p
      v-html="
        $t('portfolio.neysla.paragraphSameAttributes', {
          method: 'POST',
          purple: 'text-white-purple'
        })
      "
    />

    <h3>{{ $t("portfolio.neysla.progress.title") }}</h3>
    <pre v-highlight class="highlight">
      <code class="javascript">
...
  const file = document.getElementById("myFile").files[0];
  Neysla.post({
    url: "https://www.my-domain.com/api/your-endpoint?param=paramValue",
    headers: {
      "Authentication": "Basic dufhspo43p43hu34h5p34hu5p34"
    },
    requestType: "multipart",
    body: {
      file,
      name: "My file"
    }
    progress(progressEv){
      // {{ $t('portfolio.neysla.progress.codeComment') }}
    }
  }).then(data => console.log(data)).catch(error => console.log(error));
...
      </code>
    </pre>
    <p>
      <span
        v-html="$t('portfolio.neysla.progress.paragraphA', { purple: 'text-white-purple' })"
      ></span>
      <a
        href="https://developer.mozilla.org/en-US/docs/Web/API/XMLHttpRequest/progress_event"
        class="general-anchor decorated"
        rel="noreferrer,noopener"
        target="_blank"
      >
        <span>progress</span>
      </a>
      <span
        v-html="$t('portfolio.neysla.progress.paragraphB', { purple: 'text-white-purple' })"
      ></span>
    </p>

    <h3>{{ $t("portfolio.neysla.response.title") }}</h3>
    <p v-html="$t('portfolio.neysla.response.paragraph', { purple: 'text-white-purple' })"></p>
    <datatable :title="$props.responseTitle" :data="$props.responseData"></datatable>
    <p>
      *
      <small v-html="$t('portfolio.neysla.response.note', { purple: 'text-white-purple' })"></small>
    </p>
  </div>
</template>

<script>
const Datatable = () =>
  import(/* webpackChunkName: "Datatable" */ "@/components/Global/Datatable.vue");

export default {
  name: "NeyslaDirectUsage",
  components: {
    Datatable
  },
  props: {
    /*
     * @property - responseTitle
     * Sets response table titles
     */
    responseTitle: {
      type: Array,
      required: true
    },
    /*
     * @property - responseData
     * Sets response table data
     */
    responseData: {
      type: Array,
      required: true
    }
  },
  computed: {
    /*
     * @computed - directRequestTitle
     * Adds table header values to direct request data table
     */
    directRequestTitle() {
      return [
        {
          name: "attribute",
          displayName: this.$t(
            "portfolio.neysla.directUsage.directRequestTable.title.columnAttribute"
          )
        },
        {
          name: "description",
          displayName: this.$t(
            "portfolio.neysla.directUsage.directRequestTable.title.columnDescription"
          )
        },
        {
          name: "default",
          displayName: this.$t(
            "portfolio.neysla.directUsage.directRequestTable.title.columnDefault"
          )
        },
        {
          name: "datatype",
          displayName: this.$t(
            "portfolio.neysla.directUsage.directRequestTable.title.columnDatatype"
          )
        }
      ];
    },
    /*
     * @computed - directRequestNoBodyData
     * Adds table rows values to direct request data table
     */
    directRequestNoBodyData() {
      return [
        {
          attribute: "headers",
          description: this.$t("portfolio.neysla.directUsage.directRequestTable.data.rowHeaders"),
          default: "undefined",
          datatype: "object"
        },
        {
          attribute: "url",
          description: this.$t("portfolio.neysla.directUsage.directRequestTable.data.rowUrl"),
          default: "undefined",
          datatype: "string"
        },
        {
          attribute: "requestType",
          description: this.$t(
            "portfolio.neysla.directUsage.directRequestTable.data.rowRequestType"
          ),
          default: "urlencoded",
          datatype: "string"
        },
        {
          attribute: "responseType*",
          description: this.$t(
            "portfolio.neysla.directUsage.directRequestTable.data.rowResponseType"
          ),
          default: "json",
          datatype: "string"
        }
      ];
    },
    /*
     * @computed - directRequestBodyData
     * Adds body values to direct request data table with no body row
     */
    directRequestBodyData() {
      return [
        ...this.directRequestNoBodyData,
        {
          attribute: "body",
          description: this.$t("portfolio.neysla.directUsage.directRequestTable.data.rowBody"),
          default: "undefined",
          datatype: "object"
        }
      ];
    }
  }
};
</script>
